<template>
  <v-container>
    <div class="d-flex flex flex-column map-wrapper">
      <v-dialog
        v-model="dialog"
        :fullscreen="isMobile"
        persistent
        max-width="450px"
      >
        <v-card
          class="d-flex pa-7 justify-center align-center"
          style="position: relative"
          height="450"
        >
          <v-col v-if="status === 'processing'" cols="9">
            <DvrLoader />
          </v-col>
          <div
            v-else
            class="d-flex flex-column justify-center align-center flex"
          >
            <img src="@/assets/images/payment-success.png" width="200" alt="" />
            <div class="text-h6 my-3">{{ $t('PaymentSuccessful') }}</div>
            <div class="text-caption my-3 px-7 px-sm-3">
              {{ $t('BookingConfirmed') }}
              {{ $t('InvoiceSent') }}
            </div>
            <v-btn class="mt-3 px-10" color="primary" @click="goToReservation">
              OK
            </v-btn>
          </div>
        </v-card>
      </v-dialog>
      <div v-if="isDeposit" class="text-h4 py-4 px-0">
        {{ $t('BookingDeposit') }}
      </div>
      <div v-else class="text-h4 py-4 px-0">{{ $t('Checkout') }}</div>
      <div v-if="isDeposit" pa-3 class="text-caption">
        {{
          $t('DepositExplanation', {
            amount: toCurrency(payment.amount / 100, {
              currency: payment.currency,
            }),
          })
        }}
      </div>
      <div v-else class="text-caption">
        <span
          >{{ $t('Hello') }} <b>{{ reservation.guest.full_name }}</b
          >, {{ $t('ThankYouForChoosing') }} {{ listingName }}
          {{ $t('ForYourVacationRentalStay') }}! <br />
          {{ $t('ToCompleteYourBooking') }}</span
        ><span v-if="!$vuetify.breakpoint.xs">
          {{ $t('AfterCompletion') }}
          {{ $t('WeLookForwardToHostingYou') }}</span
        >
      </div>
      <v-row no-gutters>
        <v-col cols="12" md="6">
          <v-card class="mx-auto" max-width="800" elevation="0">
            <v-card-text class="pa-0">
              <ReservationCheckoutSummary
                v-if="isMobileOrIPad"
                :reservation="reservation"
                :payment="payment"
                class="mb-4"
              />

              <tokenizer
                v-if="tokenizerId"
                :payment-intent-id="payment.payment_intent_id"
                button-text="Confirm & pay"
                :reservation="reservation"
                @payment-success="paymentSuccess"
                @payment-processing="paymentProcessing"
                @payment-failed="paymentFailed"
              />
              <spreedly
                v-else-if="spreedlyEnvironmentKey"
                :environment-key="spreedlyEnvironmentKey"
                :payment-intent-id="payment.payment_intent_id"
                button-text="Confirm & pay"
                @payment-success="paymentSuccess"
                @payment-processing="paymentProcessing"
                @payment-failed="paymentFailed"
                :reservation="reservation"
              />
              <bridger-blox
                v-else-if="bridgerpayCredentials"
                :session-token="bridgerpayCredentials.cashier_token"
                :intent-id="payment.payment_intent_id"
                :initial-name="reservation.guest.full_name"
                :initial-email="reservation.guest.primary_email"
                :initial-phone="reservation.guest.primary_phone"
                :require-address-fields="requireAddressFields"
                :show-apple-pay="showApplePay"
                @payment-processing="paymentProcessing"
                @payment-success="paymentSuccess"
                @payment-failed="paymentFailed"
              />

              <StripeElement
                v-else-if="publicKey && reservation && !spreedlyEnvironmentKey"
                :public-key="publicKey"
                :client-secret="clientSecret"
                button-text="Confirm & pay"
                :reservation="reservation"
                @payment-success="paymentSuccess"
                @payment-processing="paymentProcessing"
                @payment-failed="paymentFailed"
              />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="0" md="1" />
        <v-col v-if="!isMobileOrIPad" cols="12" md="5">
          <div class="d-flex justify-center">
            <v-col cols="12" xl="8" class="ml-md-8 pa-0">
              <ReservationCheckoutSummary
                :reservation="reservation"
                :payment="payment"
              />
            </v-col>
          </div>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import StripeElement from 'components/stripe/StripeCustomElement'
import deviceMixin from 'components/mixins/deviceMixin'
import commonFunctions from 'components/mixins/common-functions'
import DvrLoader from '@/assets/icons/dvr-loader'
import axios from 'axios'
import ReservationCheckoutSummary from '@/components/reservations/reservation-checkout-summary'
import { HOTJAR_EVENTS } from '@/lib/analytics/hotjar'
import { mapActions } from 'vuex'
import get from 'lodash/fp/get'
import Spreedly from 'components/spreedly'
import BridgerBlox from 'components/bridgerpay/bridger-blox'
import Tokenizer from '@/components/tokenizer/tokenizer'

export default {
  name: 'ReservationCheckout',
  components: {
    ReservationCheckoutSummary,
    DvrLoader,
    StripeElement,
    Spreedly,
    BridgerBlox,
    Tokenizer,
  },
  mixins: [deviceMixin, commonFunctions],
  data() {
    return {
      dialog: false,
      status: null,
      mainReservationCode: null,
      clientSecret: '',
      publicKey: '',
      spreedlyEnvironmentKey: '',
      bridgerpayCredentials: null,
      tokenizerId: '',
      requireAddressFields: false,
      showApplePay: false,
      listing: {},
      payment: {},
      reservation: { listing: {}, guest: {}, invoice_items: [] },
    }
  },
  computed: {
    paymentIntentId() {
      return this.$route.params.intent_id
    },
    isDeposit() {
      return this.payment.capture_method === 'manual'
    },
    listingName() {
      return get('listing.resort_name', this.reservation)
    },
  },
  async mounted() {
    try {
      const {
        data: {
          payment,
          stripe_public_key,
          reservation,
          spreedly_environment_key,
          bridgerpay_credentials,
          bt_public_api_key,
          require_address_fields,
          show_apple_pay,
        },
      } = await axios.get(`/api/checkout-info/${this.paymentIntentId}`)

      if (
        payment.status === 'succeeded' ||
        payment.status === 'requires_capture' ||
        (this.reservation.balance_due === 0 &&
          payment.metadata.type === 'accommodation')
      ) {
        this.status = 'success'
        this.dialog = true
        return
      }
      this.reservation = reservation
      this.clientSecret = payment.client_secret
      this.publicKey = stripe_public_key
      this.spreedlyEnvironmentKey = spreedly_environment_key
      this.bridgerpayCredentials = bridgerpay_credentials
      this.tokenizerId = bt_public_api_key
      this.requireAddressFields = require_address_fields
      this.showApplePay = show_apple_pay

      this.payment = payment
    } catch (err) {
      this.$store.commit('updateLoading', false)
      if (err.response.status === 404) {
        this.$store.commit('showNotFound')
      }
    }
  },
  methods: {
    ...mapActions(['logAction']),
    paymentSuccess({ user }) {
      this.logAction({
        eventType: HOTJAR_EVENTS.PAYMENT_SUCCESSFUL,
        reservationId: this.reservation.id,
      })
      this.dialog = true
      setTimeout(() => {
        this.$store.dispatch('getTouristTripInfo', {
          reservationConfimation: this.$route.params.id,
          reservationId: this.reservation.reservation_guesty_id,
        })
        this.$store.dispatch('getReservations')
        this.$store.dispatch('getTouristPreCheckIn', this.$route.params.id)
        this.status = 'success'
        this.user = user
      }, 3000)
    },
    paymentProcessing() {
      this.logAction({
        eventType: HOTJAR_EVENTS.PAYMENT_ATTEMPT,
        reservationId: this.reservation.id,
      })
      this.status = 'processing'
      this.dialog = true
    },
    paymentFailed() {
      this.dialog = false
      this.status = 'failed'
    },
    goToReservation() {
      window.location.href = `/tourist/trips/${this.$route.params.id}`
    },
  },
}
</script>

<style scoped></style>
